import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type ReactNodeViewRendererOptions,
} from '@tiptap/react';
import { InstagramEmbed } from 'react-social-media-embed';
import { isValidInstagramUrl, extractInstagramData } from './instagram-utils';

const InstagramComponent = ({ node }: { node: Partial<ReactNodeViewRendererOptions> }) => {
  const url = (node?.attrs as any)?.src;

  if (!url) {
    return (
      <NodeViewWrapper>
        <div
          data-instagram-url={url}
          style={{ textAlign: 'center', padding: '20px', color: '#dc3545' }}>
          No Instagram URL provided
        </div>
      </NodeViewWrapper>
    );
  }

  const instagramData = extractInstagramData(url);
  if (!instagramData?.contentType || !instagramData?.contentId) {
    return (
      <NodeViewWrapper>
        <div
          data-instagram-url={url}
          style={{ textAlign: 'center', padding: '20px', color: '#dc3545' }}>
          Invalid Instagram URL
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <div data-instagram-url={url}>
        <InstagramEmbed url={url} width="100%" style={{ maxWidth: 550 }} captioned />
      </div>
    </NodeViewWrapper>
  );
};

export interface InstagramOptions {
  addPasteHandler: boolean;
  HTMLAttributes: Record<string, any>;
  inline: boolean;
}

type SetInstagramOptions = { src: string };

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    instagram: {
      setInstagram: (options: SetInstagramOptions) => ReturnType;
    };
  }
}

export const Instagram = Node.create<InstagramOptions>({
  name: 'instagram',

  addOptions() {
    return {
      addPasteHandler: true,
      HTMLAttributes: {},
      inline: false,
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(InstagramComponent, { attrs: this.options.HTMLAttributes });
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-instagram-url]',
      },
    ];
  },

  addCommands() {
    return {
      setInstagram:
        (options: SetInstagramOptions) =>
        ({ commands }) => {
          if (!isValidInstagramUrl(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addProseMirrorPlugins() {
    if (!this.options.addPasteHandler) {
      return [];
    }

    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            if (!event.clipboardData?.getData('text/plain')) {
              return false;
            }

            const text = event.clipboardData.getData('text/plain');

            // Only process if text contains Instagram URLs
            if (!text.includes('instagram.com')) {
              return false;
            }

            // Find all Instagram URLs in the text
            const instagramRegex = /https:\/\/[^\s\n]*instagram\.com[^\s\n]*/g;
            const matches = text.match(instagramRegex);

            if (matches && matches.length > 0) {
              const { from, to } = view.state.selection;
              let tr = view.state.tr.delete(from, to);

              matches.forEach((url, index) => {
                const instagramNode = this.type.create({ src: url });
                tr = tr.insert(tr.selection.from, instagramNode);

                if (index < matches.length - 1) {
                  tr = tr.insert(tr.selection.from, view.state.schema.text('\n'));
                }
              });

              view.dispatch(tr);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    return ['div', mergeAttributes({ 'data-instagram-url': node.attrs.src }, HTMLAttributes)];
  },
});
